// Qui se sert du logiciel chez un client — vu par l'éditeur.
//
// POURQUOI CET ÉCRAN EXISTE. Le 19/08/2026, une requête SQL à la main a montré
// ce qu'aucun écran ne disait : chez Alpha Course, le patron utilisait le
// logiciel tous les jours pendant que DEUX comptes sur trois n'avaient
// quasiment jamais servi — l'un ne s'était jamais connecté.
//
// Un client ne résilie pas du jour au lendemain : il cesse d'abord de venir.
// Sans cet écran on l'apprend à la résiliation ; avec, on l'appelle avant.
//
// CE QU'IL N'AFFICHE PAS, et c'est délibéré : aucun contenu, aucun détail
// d'action. Un compte, un compteur, une date. Le journal d'audit — bouton
// voisin — reste le seul endroit où l'on voit ce qui a été fait.
import { useEffect, useState } from "react";

import api from "../api";
import { formaterDate } from "../format";

// Au-delà de ce délai sans activité, un client mérite un appel. Deux semaines :
// assez pour ne pas s'alarmer d'une semaine de congés, assez peu pour agir
// avant que le désintérêt devienne une résiliation.
const JOURS_AVANT_ALERTE = 14;

export function joursDepuis(date) {
  if (!date) return null;
  const ms = Date.now() - new Date(date).getTime();
  return Math.floor(ms / 86400000);
}

/** « aujourd'hui », « hier », « il y a 12 jours », « jamais ».
 *
 *  Une date brute (« 04/08/2026 ») oblige à calculer de tête pour savoir si
 *  c'est inquiétant. Le délai, lui, se lit directement — et c'est le délai
 *  qui compte ici, pas la date. */
export function depuisQuand(date) {
  const j = joursDepuis(date);
  if (j === null) return "jamais";
  if (j <= 0) return "aujourd'hui";
  if (j === 1) return "hier";
  return `il y a ${j} jours`;
}

export default function UsageClient({ client, onFermer }) {
  const [usage, setUsage] = useState(null);
  const [erreur, setErreur] = useState("");

  useEffect(() => {
    api
      .get(`/plateforme/clients/${client.id}/usage`)
      .then(({ data }) => setUsage(data))
      .catch(() => setErreur("Impossible de charger l’usage de ce client."));
  }, [client.id]);

  const jours = usage ? joursDepuis(usage.derniere_activite) : null;
  const decroche = jours === null || jours >= JOURS_AVANT_ALERTE;

  return (
    <div className="modal-overlay" onClick={onFermer}>
      <div className="modal-carte modal-large" onClick={(e) => e.stopPropagation()}>
        <h3 className="modal-titre">Usage du logiciel</h3>
        <p className="modal-sous-titre">{client.nom}</p>

        {erreur && <p className="erreur">{erreur}</p>}
        {!usage && !erreur && <p className="note-discrete">Chargement…</p>}

        {usage && (
          <>
            <div className="usage-resume">
              <div className="usage-chiffre">
                <strong>{usage.actions_7j}</strong>
                <span>actions sur 7 jours</span>
              </div>
              <div className="usage-chiffre">
                <strong>{usage.actions_30j}</strong>
                <span>sur 30 jours</span>
              </div>
              <div className={"usage-chiffre" + (decroche ? " usage-alerte" : "")}>
                <strong>{depuisQuand(usage.derniere_activite)}</strong>
                <span>dernière activité</span>
              </div>
            </div>

            {/* LE CHIFFRE QUI DÉCLENCHE UN APPEL : des accès facturés que
                personne n'utilise. Un compte désactivé n'y figure pas — il est
                fermé, c'est voulu, et les confondre ferait appeler un client
                pour un départ qu'il a lui-même organisé. */}
            {usage.comptes_dormants > 0 && (
              <p className="sous-info">
                <strong>
                  {usage.comptes_dormants} compte
                  {usage.comptes_dormants > 1 ? "s actifs n’ont" : " actif n’a"} rien
                  fait depuis 30 jours.
                </strong>{" "}
                Des accès ouverts que personne n’utilise : soit ils n’ont jamais
                reçu leurs identifiants, soit ils ont essayé et abandonné. La
                seconde raison est celle qui vous intéresse.
              </p>
            )}

            {decroche && usage.actions_30j > 0 && (
              <p className="sous-info">
                <strong>Ce client ne vient plus.</strong> Il s’est servi du
                logiciel ({usage.actions_30j} actions sur 30 jours) puis a cessé.
                Un client ne résilie pas d’un coup : il arrête d’abord de venir.
              </p>
            )}

            <table className="tableau">
              <thead>
                <tr>
                  <th>Compte</th>
                  <th>Rôle</th>
                  <th>30 jours</th>
                  <th>Dernière fois</th>
                </tr>
              </thead>
              <tbody>
                {usage.comptes.map((u) => (
                  <tr key={u.email}>
                    <td>
                      <strong>{u.nom_complet}</strong>
                      <br />
                      <span className="sous-info">{u.email}</span>
                      {!u.actif && (
                        <span className="note-discrete"> · désactivé</span>
                      )}
                    </td>
                    <td>{u.role}</td>
                    <td>{u.actions_30j}</td>
                    <td
                      className={
                        u.actif && !u.derniere_activite ? "lien-danger" : ""
                      }
                      title={
                        u.derniere_activite
                          ? formaterDate(u.derniere_activite)
                          : "Ce compte ne s’est jamais connecté"
                      }
                    >
                      {depuisQuand(u.derniere_activite)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <p className="note-discrete">
              Cet écran résume, il n’élargit rien : aucun contenu, aucun détail
              d’action. Pour voir ce qui a été fait, ouvrir le <strong>Journal</strong>.
            </p>
          </>
        )}

        <div className="modal-actions">
          <button className="btn" onClick={onFermer}>
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}
